import { Injectable } from '@angular/core';
import { Segment } from '@app/models/micro/segment.model';
import { Passenger } from '@app/models/micro/passenger.model';
import { Reservation } from '@app/models/micro/reservation.model';
import { SeatMap } from '@app/models/seatMap.model';
import {
  ExtrasBagsAncillarieInt,
  LanguagesInt,
  PNRCollectionInt,
  PassportScanInfoInt,
  BoardingPassResponseInt,
  PCCInt,
} from '@interfaces/';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { AdminService } from '@services/admin.service';
import { PnrAWSMapper } from '@app/mappers/pnr-aws.mapper';
import { PrintErrorGuardService } from '@services/devices';

/**
 * Servicio de gestión global de datos y configuración de la aplicación.
 *
 * Este servicio centraliza la gestión de datos relacionados con la reserva, pasajeros,
 * configuraciones de idioma, flujo de pagos, etc., y los guarda en el almacenamiento
 * de sesión. Permite el acceso y actualización de la información global de la aplicación
 * y facilita la comunicación entre diferentes componentes de la misma.
 *
 * También se encarga de manejar operaciones comunes como la configuración y recuperación
 * de información relacionada con el PNR, los pasajeros, la reserva, la reimpresión de
 * tarjetas de embarque, entre otros.
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalsService {
  language$ = new BehaviorSubject<string>('es_MX');
  disableBagsFlow$ = new BehaviorSubject<boolean>(false);
  languages: Array<LanguagesInt> = [
    {
      id: 'es-MX',
      text: 'Español',
    },
    {
      id: 'en-US',
      text: 'English',
    },
    {
      id: 'fr-FR',
      text: 'Français',
    },
    {
      id: 'du-NL',
      text: 'Nederlands',
    },
    {
      id: 'pt-PT',
      text: 'Português',
    },
    {
      id: 'ja-JP',
      text: '日本語',
    },
    {
      id: 'zh-CN',
      text: '中文',
    },
    {
      id: 'kr-KR',
      text: '한국어',
    },
  ];
  languageSelected: LanguagesInt = this.languages[0];
  pnrLookUpApi: PNRCollectionInt;
  pnrLookUp: Array<Reservation> = [];
  selectedTrip: Segment | null = null;
  seapMapInfoArray: Array<SeatMap> = [];
  passengerSelected: Passenger | null = null;
  passportInfoToPnrLookUp: PassportScanInfoInt;

  reservation: Reservation | null = null;
  selectedSegment: Segment | null = null;
  ancillaries: ExtrasBagsAncillarieInt;
  reloadPnr: boolean;
  isMultiFlightsOpt = false;
  isFlowFF: boolean = false;
  isFlowVeryPassport: boolean = false;
  pcc: PCCInt | null = null;
  isFlowMYBClosed: boolean = false;
  showModalPriority: boolean = true;
  originFlowBags: string = null;
  constructor(
    private _traslateService: TranslateService,
    private _admin: AdminService,
    private _printErrorGuard: PrintErrorGuardService
  ) {}

  /**
   * Establece la información de la consulta de PNR en el almacenamiento de sesión.
   *
   * @param {PNRCollectionInt} pnrLookUpApi - Objeto que contiene la información de la consulta del PNR.
   */

  setPNRLookUpApi(pnrLookUpApi: PNRCollectionInt) {
    if (pnrLookUpApi || Object.keys(pnrLookUpApi).length > 0) {
      this.pnrLookUpApi = pnrLookUpApi;
      sessionStorage.setItem('pnrLookUpApi', JSON.stringify(pnrLookUpApi));
    }
  }

  /**
   * Establece la lista de reservas en el almacenamiento de sesión.
   *
   * @param {Array<Reservation>} reservation - Lista de reservas a almacenar.
   */

  setPNRLookUp(reservation: Array<Reservation>): void {
    if (reservation || Object.keys(reservation).length > 0) {
      this.pnrLookUp = reservation;
      sessionStorage.setItem('pnrLookUp', JSON.stringify(reservation));
    }
  }

  setPNRLookupAWSInt(obj) {
    sessionStorage.setItem('PNRLookupAWSInt', JSON.stringify(obj));
  }

  getPNRLookupAWSInt() {
    const PNRLookupAWSInt = sessionStorage.getItem('PNRLookupAWSInt');
    return PNRLookupAWSInt ? JSON.parse(PNRLookupAWSInt) : {};
  }

  /**
   * Recupera la lista de reservas almacenadas en el almacenamiento de sesión o en memoria.
   *
   * @returns {Array<Reservation>} - Lista de reservas.
   */

  getPNRLookup(): Array<Reservation> {
    const { Location } = this._admin.admin;
    if (this.pnrLookUp?.length > 0) {
      return this.pnrLookUp;
    } else {
      const sessionPNR = sessionStorage.getItem('pnrLookUpApi');
      if (sessionPNR) {
        this.pnrLookUp = PnrAWSMapper.pnrBuild(JSON.parse(sessionPNR), Location);
        return this.pnrLookUp;
      } else {
        return [];
      }
    }
  }

  setReservation(reservation: Reservation): void {
    if (reservation || Object.keys(reservation).length > 0) {
      this.reservation = reservation;
      sessionStorage.setItem('reservation', JSON.stringify(reservation));
    }
  }

  /**
   * Obtiene la reserva actual.
   *
   * Este método verifica primero si la reserva ya está almacenada en la propiedad `reservation` de la clase.
   * Si no está disponible allí, intenta obtenerla del almacenamiento de sesión (sessionStorage).
   * Si encuentra la reserva en sessionStorage, la deserializa y la asigna a la propiedad `reservation`.
   *
   * @returns {Reservation} - El objeto de reserva actual. Si no se encuentra ninguna reserva, se retorna una nueva instancia de `Reservation`.
   */

  getReservation(): Reservation {
    if (this.reservation) {
      return this.reservation;
    }
    let sessionReservation = sessionStorage.getItem('reservation');
    this.reservation = new Reservation();
    if (sessionReservation) {
      sessionReservation = JSON.parse(sessionReservation);
      Object.assign(this.reservation, sessionReservation);
    }
    return this.reservation;
  }

  setSelectedTrip(segment: Segment): void {
    if (segment) {
      this.selectedTrip = segment;
      sessionStorage.setItem('selectedTrip', JSON.stringify(segment));
    }
  }

  /**
   * Obtiene el segmento de viaje seleccionado.
   *
   * Este método primero verifica si la propiedad `selectedTrip` ya contiene un valor.
   * Si es así, lo devuelve directamente. Si no, intenta recuperar el segmento de viaje desde el
   * almacenamiento de sesión (sessionStorage). Si se encuentra, se deserializa y asigna a la propiedad `selectedTrip`.
   *
   * @returns {Segment} - El segmento de viaje seleccionado. Si no se encuentra ningún valor, se devuelve un nuevo objeto de tipo `Segment`.
   */

  getSelectedTrip(): Segment {
    if (this.selectedTrip) {
      return this.selectedTrip;
    } else {
      let segmentSelectedTrip = sessionStorage.getItem('selectedTrip') || '';
      this.selectedTrip = new Segment();

      if (segmentSelectedTrip) {
        segmentSelectedTrip = JSON.parse(segmentSelectedTrip);
        Object.assign(this.selectedTrip, segmentSelectedTrip);
      }

      return this.selectedTrip;
    }
  }

  setDataPassportInfoToPnrLookUp(passportInfo: PassportScanInfoInt) {
    this.passportInfoToPnrLookUp = passportInfo;
  }

  getDataPassportInfoToPnrLookUp() {
    return this.passportInfoToPnrLookUp;
  }

  clearDataPassportInfoToPnrLookUp() {
    this.passportInfoToPnrLookUp = undefined;
  }
  /**
   * Establece o actualiza la información de un pasajero en la reserva.
   *
   * Este método toma un objeto de tipo `Passenger`, busca el pasajero correspondiente en la lista de pasajeros
   * de la reserva, y si lo encuentra, actualiza la información del pasajero con los nuevos datos proporcionados.
   * Luego, la lista actualizada de pasajeros se asigna nuevamente a la reserva y se guarda utilizando el método `setReservation`.
   *
   * @param {Passenger} passenger - El objeto de tipo `Passenger` que contiene la información actualizada del pasajero.
   */
  setPassenger(passenger: Passenger): void {
    if (passenger || Object.keys(passenger).length > 0) {
      let passengers = this.getPassengers();
      console.log(' passengers 1 ', passengers);

      passengers.map((passenger_) => {
        if (passenger_.id === passenger.id) {
          Object.assign(passenger_, passenger);
        }
      });
      console.log(' passengers 2 ', passengers);
      this.reservation.passengers = passengers;
      this.setReservation(this.reservation);
    }
  }

  getPassengers(): Array<Passenger> {
    if (this.reservation?.passengers?.length > 0) {
      const deduped = this.dedupePassengers(this.reservation.passengers);
      if (deduped.length !== this.reservation.passengers.length) {
        this.reservation.passengers = deduped;
        this.setReservation(this.reservation);
      }
      return this.reservation.passengers;
    }
    let sessionPassengerTrip = this.getReservation().passengers;
    sessionPassengerTrip = sessionPassengerTrip || [];
    let passengersTrip = [];
    for (let passenger of sessionPassengerTrip) {
      let passenger_ = new Passenger();
      Object.assign(passenger_, passenger);
      passengersTrip.push(passenger_);
    }
    return this.dedupePassengers(passengersTrip);
  }

  setBoardingPassPrinter(boardingPassPrinter: BoardingPassResponseInt) {
    boardingPassPrinter.value = boardingPassPrinter.value.filter((x) => x);
    sessionStorage.setItem('boardingPassPrinter', JSON.stringify(boardingPassPrinter));
  }

  getBoardingPassPrinter(): BoardingPassResponseInt {
    const boardingPass = sessionStorage.getItem('boardingPassPrinter');
    return boardingPass ? JSON.parse(boardingPass) : undefined;
  }

  setOverbookingFlow(yesNo) {
    sessionStorage.setItem('overBookingFlow', JSON.stringify(yesNo));
  }

  setPhoneCode(value) {
    sessionStorage.setItem('phoneCode', JSON.stringify(value));
  }

  getPhoneCode() {
    const phoneCode = sessionStorage.getItem('phoneCode');
    return phoneCode ? JSON.parse(phoneCode) : undefined;
  }

  getOverbookingFlow() {
    const overBookingFlow = sessionStorage.getItem('overBookingFlow');

    return overBookingFlow ? JSON.parse(overBookingFlow) : false;
  }

  setCurrentBagTag(obj) {
    sessionStorage.setItem('currentBagTag', JSON.stringify(obj));
  }

  getCurrentBagTag() {
    const currentBagTag = sessionStorage.getItem('currentBagTag');

    return currentBagTag ? JSON.parse(currentBagTag) : {};
  }

  clearPNRLookupAWSInt(): void {
    sessionStorage.removeItem('PNRLookupAWSInt');
  }
  clearBoardingPassPrinter(): void {
    sessionStorage.removeItem('boardingPassPrinter');
  }
  clearAncillaries(): void {
    this.ancillaries = null;
    sessionStorage.removeItem('ancillaries');
  }
  clearPNRLookUp(): void {
    this.pnrLookUp = null;
    sessionStorage.removeItem('pnrLookUp');
  }
  clearSelectTrip(): void {
    this.selectedTrip = null;
    sessionStorage.removeItem('selectedTrip');
  }
  clearReservation(): void {
    this.reservation = null;
    sessionStorage.removeItem('reservation');
  }
  /**
   * Limpia todos los datos almacenados en sesión relacionados con PNR, selección de vuelo, reserva, y más.
   */
  clearAll() {
    this.clearPNRLookupAWSInt();
    this.clearBoardingPassPrinter();
    this.clearAncillaries();
    this.clearPNRLookUp();
    this.clearSelectTrip();
    this.clearReservation();
    this.clearDataPassportInfoToPnrLookUp();
    this._printErrorGuard.reset();
    this.language$.next(this._admin.admin?.DEFAULT_LANGUAGE);
    this._traslateService.use(this._admin.admin?.DEFAULT_LANGUAGE);
  }

  /**
   * Abrevia una cadena de texto a una longitud máxima, añadiendo "..." si es necesario.
   *
   * @param {string} string - Cadena a abreviar.
   * @param {number} maxLength - Longitud máxima permitida para la cadena.
   * @returns {string} - Cadena abreviada.
   */

  abbreviateString(string, maxLength) {
    if (string == null || maxLength == null) {
      return string;
    }
    var newString = string;
    if (newString.length >= maxLength) {
      newString = newString.substring(0, maxLength - 2);
      newString += '..';
    }
    return newString;
  }

  /**
   * Separa una lista en grupos de tamaño específico.
   *
   * @param {Array<any>} list - Lista de elementos a separar.
   * @param {number} sizeOfGroup - Tamaño de cada grupo.
   * @returns {Array<Array<any>>} - Lista de grupos.
   * @throws "Null Not Allowed" si la lista o el tamaño del grupo son nulos.
   */

  separateIntoGroups(list, sizeOfGroup) {
    if (list == null || sizeOfGroup == null) {
      throw 'Null Not Allowed';
    }
    var groups = [];
    var groupIndex = 0;
    for (var index = 0; index < list.length; index++) {
      var passenger = list[index];
      if (groups[groupIndex] == null) {
        groups[groupIndex] = [];
      }
      groups[groupIndex].push(passenger);
      if (groups[groupIndex].length == sizeOfGroup) {
        groupIndex++;
      }
    }
    return groups;
  }

  buildReprintBoardingPassInput(passengers: Passenger[]) {
    const passengerIds = passengers?.map((x) => x.id).filter((id) => id != null);
    return {
      pnrCode: this.reservation.pnr,
      language: this._traslateService.currentLang,
      legCode: this.reservation.flightSummary.legCode,
      passengerIds: passengerIds,
    };
  }

  getAncillaries(): ExtrasBagsAncillarieInt {
    if (this.ancillaries) {
      return this.ancillaries;
    }
    let ancillariesStorage = sessionStorage.getItem('ancillaries');
    this.ancillaries = {};
    if (ancillariesStorage) {
      ancillariesStorage = JSON.parse(ancillariesStorage);
      Object.assign(this.ancillaries, ancillariesStorage);
    }
    return this.ancillaries;
  }

  setAncillaries(obj) {
    sessionStorage.setItem('ancillaries', JSON.stringify(obj));
  }

  setSeatMapInfo(seat) {
    if (seat) {
      sessionStorage.setItem('SeatMapInfo', JSON.stringify(seat));
    }
  }

  getSeatMapInfo() {
    let SeatMapSession = sessionStorage.getItem('SeatMapInfo');
    SeatMapSession = SeatMapSession ? JSON.parse(SeatMapSession) : [];
    this.seapMapInfoArray = [];
    for (let seat of SeatMapSession) {
      let seat_ = new SeatMap();
      Object.assign(seat_, seat);
      this.seapMapInfoArray.push(seat_);
    }
    return this.seapMapInfoArray;
  }

  setSeatMapSegment(obj) {
    sessionStorage.setItem('SeatMap', JSON.stringify(obj));
  }

  getSeatMapSegmet() {
    const SeatMap = sessionStorage.getItem('SeatMap');
    return SeatMap ? JSON.parse(SeatMap) : {};
  }

  clearSesionStorage() {
    sessionStorage.clear();
    localStorage.clear();
  }

  set isMultiFlights(isMultiple: boolean) {
    this.isMultiFlightsOpt = isMultiple;
  }

  get isMultiFlights(): boolean {
    return this.isMultiFlightsOpt;
  }

  /**
   * Elimina pasajeros duplicados (mismo nameRefNumber o mismo id) que a veces vienen duplicados en el JSON de reserva.
   * Mantiene la primera aparición y el orden original.
   */
  private dedupePassengers(passengers: Passenger[]): Passenger[] {
    if (!passengers?.length) {
      return passengers;
    }
    const seenKeys = new Set<string>();
    const out: Passenger[] = [];
    for (const p of passengers) {
      const key = this.passengerIdentityKey(p);
      if (key) {
        if (seenKeys.has(key)) {
          continue;
        }
        seenKeys.add(key);
      }
      out.push(p);
    }
    return out;
  }

  private passengerIdentityKey(p: Passenger): string | null {
    const nr = p.nameRefNumber?.trim();
    if (nr) {
      return `nr:${nr}`;
    }
    if (p.id != null && String(p.id).length > 0) {
      return `id:${p.id}`;
    }
    if (p.passengerId != null && String(p.passengerId).length > 0) {
      return `pid:${p.passengerId}`;
    }
    return null;
  }
}
